// Current Session Enrollment — the live registration roster inside the Class
// Editor (Registration tab).
//
// SINGLE SOURCE OF TRUTH: every number here comes from the same records the
// public registration flow writes and the rest of the system reads —
//   • seats / capacity  → src/lib/enrollment.ts (enrollmentFor / rosterFor)
//   • money             → financial_ledger_entries (Financial Ledger)
//   • payroll           → payroll engine action `class_payroll_summary`
// Nothing is recalculated locally.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Mail,
  Search,
  RefreshCw,
  Trash2,
  Eye,
  Users,
  ListPlus,
  DollarSign,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  enrollmentFor,
  isArchivedSessionLabel,
  isPaid,
  parseSelectedSessions,
  sessionLabelMatches,
  interestFamiliesFor,
  type RegistrationChildRow,
  type RegistrationRow,
  type WaitlistRow,
} from "@/lib/enrollment";
import { getClassAliases } from "@/lib/classIdentity";
import { listClassSessionOptions, type SessionOption } from "@/lib/nextClassSession";
import { getClassPayrollSummary, money, type ClassPayrollSummary } from "@/lib/payroll";
import type { ClassSession } from "@/data/classes";
import { WelcomePacketView } from "@/components/welcome-packet/WelcomePacketView";
import { useAuth } from "@/contexts/AuthContext";
import RefundDialog from "@/components/admin/refunds/RefundDialog";
import RefundHistory from "@/components/admin/refunds/RefundHistory";
import ProratedEnrollmentPanel from "@/components/admin/classes/ProratedEnrollmentPanel";


interface Props {
  /** Database id of the class (uuid). Panel is hidden without it. */
  dbClassId?: string;
  slug: string;
  title: string;
  capacity: number;
  minEnrollment: number;
  registrationOpen: boolean;
  sessions: ClassSession[];
  scheduleText: string;
}

type PaymentState = "paid" | "pending" | "refunded" | "failed";

interface RosterRow {
  key: string;
  paymentId: string;
  studentName: string;
  parentName: string;
  email: string;
  registeredAt: string;
  amountCents: number;
  /** Whole class_payment total — the unit the refund service works in. */
  paymentAmountCents: number;
  refundedCents: number;
  sessionLabel: string | null;
  state: PaymentState;
  hidden: boolean;
  stripeSessionId: string | null;
}

interface LedgerRow {
  id: string;
  direction: string;
  category: string;
  entry_kind: string;
  amount_cents: number;
  voided_at: string | null;
  related_session_label: string | null;
}

type FilterKey = "all" | "paid" | "pending" | "refunded" | "waitlist";

const fmtDate = (iso: string) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "—";

export default function ClassRosterPanel({
  dbClassId,
  slug,
  title,
  capacity,
  minEnrollment,
  registrationOpen,
  sessions,
  scheduleText,
}: Props) {
  const aliases = useMemo(
    () => Array.from(new Set([...getClassAliases(dbClassId ?? ""), ...getClassAliases(slug)])).filter(Boolean),
    [dbClassId, slug],
  );

  const sessionOptions = useMemo<SessionOption[]>(
    () => listClassSessionOptions({ sessions, scheduleText }),
    [sessions, scheduleText],
  );
  const liveOptions = sessionOptions.filter((s) => s.state !== "past");
  const pastOptions = sessionOptions.filter((s) => s.state === "past");

  const [selected, setSelected] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<RegistrationRow[]>([]);
  const [children, setChildren] = useState<RegistrationChildRow[]>([]);
  const [interest, setInterest] = useState<WaitlistRow[]>([]);
  const [ledger, setLedger] = useState<LedgerRow[]>([]);
  const [payroll, setPayroll] = useState<ClassPayrollSummary | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<FilterKey>("all");
  const [detail, setDetail] = useState<RosterRow | null>(null);
  const [refundRow, setRefundRow] = useState<RosterRow | null>(null);
  // Refund actions are admin-only in the UI; the service re-checks server-side.
  const { isAdmin } = useAuth();
  
  const [confirmRemove, setConfirmRemove] = useState<{ row: RosterRow; toWaitlist: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  // Default to the current (or next) session.
  useEffect(() => {
    if (selected || sessionOptions.length === 0) return;
    setSelected(sessionOptions[0].label);
  }, [sessionOptions, selected]);

  const selectedOption = sessionOptions.find((s) => s.label === selected) ?? null;

  const load = useCallback(async () => {
    if (!dbClassId || aliases.length === 0) return;
    setLoading(true);
    try {
      const [pRes, iRes, lRes] = await Promise.all([
        supabase
          .from("class_payments")
          .select(
            "id, class_id, adult_name, child_name, email, selected_sessions, canonical_session_label, amount_paid, refunded_amount_cents, payment_status, hidden, created_at, stripe_session_id",
          )
          .in("class_id", aliases)
          .order("created_at", { ascending: false }),
        supabase
          .from("class_interest")
          .select("id, class_id, adult_name, child_name, email, created_at, selected_session_label")
          .in("class_id", aliases)
          .order("created_at", { ascending: false }),
        supabase
          .from("financial_ledger_entries")
          .select("id, direction, category, entry_kind, amount_cents, voided_at, related_session_label")
          .in("related_class_id", aliases),
      ]);

      const paymentRows = (pRes.data ?? []) as unknown as (RegistrationRow & {
        refunded_amount_cents?: number;
      })[];
      setPayments(paymentRows as RegistrationRow[]);
      setInterest((iRes.data ?? []) as unknown as WaitlistRow[]);
      setLedger((lRes.data ?? []) as unknown as LedgerRow[]);

      const ids = paymentRows.map((p) => p.id);
      if (ids.length) {
        const { data: kids } = await supabase
          .from("class_registration_children")
          .select(
            "id, class_payment_id, class_id, session_label, child_first_name, child_last_name, child_age, grade, notes, final_child_amount, created_at",
          )
          .in("class_payment_id", ids);
        setChildren((kids ?? []) as unknown as RegistrationChildRow[]);
      } else {
        setChildren([]);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [dbClassId, aliases]);

  useEffect(() => {
    load();
  }, [load]);

  // Live updates — roster, seats, money and waitlist refresh with no reload.
  useEffect(() => {
    if (!dbClassId) return;
    const ch = supabase
      .channel(`class-roster-${dbClassId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "class_payments" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "class_registration_children" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "class_interest" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "financial_ledger_entries" }, () => load())
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [dbClassId, load]);

  // Payroll — read directly from the payroll engine for the selected session.
  useEffect(() => {
    let cancelled = false;
    if (!slug) return;
    getClassPayrollSummary(slug, selected)
      .then((r) => {
        if (!cancelled) setPayroll(r);
      })
      .catch(() => {
        if (!cancelled) setPayroll(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, selected]);

  /* ---------------- Derived roster (selected session only) ---------------- */

  const rows = useMemo<RosterRow[]>(() => {
    if (!dbClassId) return [];
    const aliasSet = new Set(aliases);
    const byId = new Map(payments.map((p) => [p.id, p]));
    const state = (p: RegistrationRow & { refunded_amount_cents?: number }): PaymentState => {
      const refunded = Number(p.refunded_amount_cents ?? 0);
      if (refunded > 0) return "refunded";
      if (isPaid(p.payment_status)) return "paid";
      const s = String(p.payment_status ?? "").toLowerCase();
      if (["failed", "cancelled", "canceled", "expired"].includes(s)) return "failed";
      return "pending";
    };

    const matching = children.filter((c) => {
      const parent = byId.get(c.class_payment_id);
      if (!parent) return false;
      const inClass = c.class_id ? aliasSet.has(String(c.class_id)) : aliasSet.has(String(parent.class_id));
      if (!inClass) return false;
      return sessionLabelMatches(c.session_label, selected);
    });
    const parentsWithChildren = new Set(matching.map((c) => c.class_payment_id));

    const childRows: RosterRow[] = matching.map((c) => {
      const parent = byId.get(c.class_payment_id) as RegistrationRow & {
        refunded_amount_cents?: number;
        stripe_session_id?: string | null;
      };
      return {
        key: `child:${c.id}`,
        paymentId: c.class_payment_id,
        studentName:
          [c.child_first_name, c.child_last_name].filter(Boolean).join(" ").trim() ||
          parent.child_name ||
          "Student",
        parentName: parent.adult_name ?? "—",
        email: parent.email ?? "",
        registeredAt: c.created_at || parent.created_at,
        amountCents: Number(c.final_child_amount ?? parent.amount_paid ?? 0),
        paymentAmountCents: Number(parent.amount_paid ?? 0),
        refundedCents: Number(parent.refunded_amount_cents ?? 0),
        sessionLabel: c.session_label,
        state: state(parent),
        hidden: !!parent.hidden,
        stripeSessionId: parent.stripe_session_id ?? null,
      };
    });

    const legacyRows: RosterRow[] = (payments as (RegistrationRow & { refunded_amount_cents?: number; canonical_session_label?: string | null; stripe_session_id?: string | null })[])
      .filter((p) => {
        if (!aliasSet.has(String(p.class_id))) return false;
        if (parentsWithChildren.has(p.id)) return false;
        if (!selected) return true;
        // Canonical occurrence assignment wins over the raw historical label,
        // exactly as in rosterFor(). Without this an archived "Session 1"
        // registration re-appears under a later "Session 1" occurrence.
        if (p.canonical_session_label) return sessionLabelMatches(p.canonical_session_label, selected);
        const labels = parseSelectedSessions(p.selected_sessions);
        if (labels.length === 0) return false;
        return labels.some((l) => sessionLabelMatches(l, selected));
      })
      .map((p) => ({
        key: `pay:${p.id}`,
        paymentId: p.id,
        studentName: p.child_name || "Student",
        parentName: p.adult_name ?? "—",
        email: p.email ?? "",
        registeredAt: p.created_at,
        amountCents: Number(p.amount_paid ?? 0),
        paymentAmountCents: Number(p.amount_paid ?? 0),
        refundedCents: Number(p.refunded_amount_cents ?? 0),
        // Prefer the resolved canonical label so historical registrations read as
        // real archived sessions instead of falling back to "No session set".
        sessionLabel: p.canonical_session_label ?? parseSelectedSessions(p.selected_sessions)[0] ?? null,

        state: state(p),
        hidden: !!p.hidden,
        stripeSessionId: p.stripe_session_id ?? null,
      }));

    return [...childRows, ...legacyRows]
      .filter((r) => !r.hidden)
      .sort((a, b) => a.studentName.localeCompare(b.studentName));
  }, [payments, children, aliases, selected, dbClassId]);

  /**
   * Registrations belonging to an ARCHIVED occurrence of a session label
   * (e.g. "Session 1 · May 2026 (archived)"). They are preserved history:
   * shown read-only, never counted toward the selected session's seats.
   */
  const historicalRows = useMemo(() => {
    const aliasSet = new Set(aliases);
    return (payments as (RegistrationRow & { canonical_session_label?: string | null })[])
      .filter((p) => aliasSet.has(String(p.class_id)) && !p.hidden)
      .map((p) => ({
        id: p.id,
        label: p.canonical_session_label ?? parseSelectedSessions(p.selected_sessions)[0] ?? null,
        student: p.child_name || "Student",
        parent: p.adult_name ?? "—",
        email: p.email ?? "",
        created: p.created_at,
        amount: Number(p.amount_paid ?? 0),
      }))
      .filter((r) => isArchivedSessionLabel(r.label))
      .sort((a, b) => String(b.created).localeCompare(String(a.created)));
  }, [payments, aliases]);


  const waitlistRows = useMemo(
    () =>
      interest.filter((i) =>
        selected
          ? sessionLabelMatches((i as WaitlistRow & { selected_session_label?: string }).selected_session_label, selected)
          : true,
      ),
    [interest, selected],
  );

  const seatsFilled = useMemo(
    () => (dbClassId ? enrollmentFor(payments, dbClassId, selected, children) : 0),
    [payments, children, dbClassId, selected],
  );
  const waitlistCount = dbClassId ? interestFamiliesFor(interest, dbClassId) : 0;
  const openSeats = Math.max(0, capacity - seatsFilled);

  /* ---------------- Financial summary (Financial Ledger) ---------------- */

  const financial = useMemo(() => {
    const scoped = ledger.filter(
      (e) => !e.voided_at && sessionLabelMatches(e.related_session_label, selected),
    );
    const collected = scoped
      .filter((e) => e.direction === "in" && e.entry_kind !== "reversal" && e.category === "Registration Revenue")
      .reduce((s, e) => s + Number(e.amount_cents || 0), 0);
    const refunds = scoped
      .filter((e) => e.direction === "out" && e.category === "Refund")
      .reduce((s, e) => s + Number(e.amount_cents || 0), 0);
    const outstanding = rows
      .filter((r) => r.state === "pending")
      .reduce((s, r) => s + r.amountCents, 0);
    return { collected, refunds, outstanding, net: collected - refunds };
  }, [ledger, selected, rows]);

  /* ---------------- Filtering ---------------- */

  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== "all" && filter !== "waitlist" && r.state !== filter) return false;
      if (filter === "waitlist") return false;
      if (!q) return true;
      return (
        r.studentName.toLowerCase().includes(q) ||
        r.parentName.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q)
      );
    });
  }, [rows, filter, query]);

  const visibleWaitlist = useMemo(() => {
    if (filter !== "all" && filter !== "waitlist") return [];
    const q = query.trim().toLowerCase();
    return waitlistRows.filter(
      (w) =>
        !q ||
        (w.child_name ?? "").toLowerCase().includes(q) ||
        (w.adult_name ?? "").toLowerCase().includes(q) ||
        (w.email ?? "").toLowerCase().includes(q),
    );
  }, [waitlistRows, filter, query]);

  /* ---------------- Actions ---------------- */

  // PHASE 3B/4A: the legacy doRefund() direct client-side write to
  // class_payments is gone (and is rejected by the database guard anyway).
  // Refunds are issued exclusively through the admin-refunds service via
  // <RefundDialog />, which owns preview, confirmation and execution.


  const doRemove = async () => {
    if (!confirmRemove) return;
    const { row, toWaitlist } = confirmRemove;
    setBusy(true);
    try {
      if (toWaitlist) {
        const { error: iErr } = await supabase.from("class_interest").insert({
          class_id: dbClassId!,
          adult_name: row.parentName,
          child_name: row.studentName,
          child_age: 0,
          email: row.email,
          selected_session_label: row.sessionLabel,
        } as never);
        if (iErr) throw iErr;
      }
      const { error } = await supabase
        .from("class_payments")
        .update({ hidden: true })
        .eq("id", row.paymentId);
      if (error) throw error;
      toast.success(
        toWaitlist
          ? `${row.studentName} moved to the waitlist.`
          : `${row.studentName} removed from the roster. Payment history is preserved.`,
      );
      setConfirmRemove(null);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!dbClassId) {
    return (
      <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
        Save this class first — the live roster appears once it exists in the database.
      </div>
    );
  }

  const filled = capacity > 0 ? Math.min(10, Math.round((seatsFilled / capacity) * 10)) : 0;
  const meter = "█".repeat(filled) + "░".repeat(Math.max(0, 10 - filled));

  return (
    <div className="rounded-md border bg-card/40">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
        <div className="inline-flex items-center gap-2 text-sm font-medium">
          <Users className="h-4 w-4 text-emerald-700" />
          Current Session Enrollment
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Viewing</span>
          <Select
            value={selected ?? ""}
            onValueChange={(v) => {
              if (v === "__past__") {
                setShowPast(true);
                return;
              }
              setSelected(v);
            }}
          >
            <SelectTrigger className="h-8 w-[260px] text-xs">
              <SelectValue placeholder="Select a session" />
            </SelectTrigger>
            <SelectContent>
              {liveOptions.map((s) => (
                <SelectItem key={s.label} value={s.label} className="text-xs">
                  {s.display}
                </SelectItem>
              ))}
              {showPast &&
                pastOptions.map((s) => (
                  <SelectItem key={s.label} value={s.label} className="text-xs">
                    {s.display} · past
                  </SelectItem>
                ))}
              {!showPast && pastOptions.length > 0 && (
                <SelectItem value="__past__" className="text-xs">
                  Load previous sessions ({pastOptions.length})
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => load()} aria-label="Refresh roster">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="space-y-4 p-3">
        {/* Welcome packet — same resolved content families receive */}
        <WelcomePacketView
          classSlug={slug}
          sessionLabel={selectedOption?.label ?? null}
          title="Welcome packet families receive"
          compact
        />

        {/* Session + summary */}
        <div className="rounded-md border bg-background/60 p-3">
          <div className="text-sm font-medium">
            {selectedOption
              ? selectedOption.display
              : sessionOptions.length === 0
                ? "All registrations · no sessions configured yet"
                : "No session selected"}
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 text-xs sm:grid-cols-2 lg:grid-cols-5">
            <Stat label="Capacity" value={`${seatsFilled} / ${capacity} enrolled`} detail={`${openSeats} seat${openSeats === 1 ? "" : "s"} available`} />
            <Stat label="Minimum enrollment target" value={`${minEnrollment} paid enrollments`} detail="Required for the class to run" />
            <Stat label="Interested families" value={String(waitlistCount)} detail="Not currently enrolled" />
            <Stat label="Open seats" value={String(openSeats)} detail={`Of ${capacity} total seats`} />
            <Stat
              label="Registration status"
              value={registrationOpen ? "Registration Open" : "Registration Closed"}
              detail={registrationOpen ? "Families can register" : "Checkout is unavailable"}
            />
          </div>
          <div className="mt-2 font-mono text-xs text-emerald-800">
            {meter} &nbsp;{seatsFilled} / {capacity} seats filled · {openSeats} remaining
          </div>
        </div>

        {/* Search + filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search student, parent, or email"
              className="h-8 pl-7 text-xs"
            />
          </div>
          {(["all", "paid", "pending", "refunded", "waitlist"] as FilterKey[]).map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? "default" : "outline"}
              className="h-8 text-xs capitalize"
              onClick={() => setFilter(f)}
            >
              {f === "waitlist" ? "interest" : f}
            </Button>
          ))}
        </div>

        {/* Roster */}
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full min-w-[640px] text-xs">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">Student</th>
                <th className="px-2 py-1.5 text-left font-medium">Parent</th>
                <th className="px-2 py-1.5 text-left font-medium">Registered</th>
                <th className="px-2 py-1.5 text-left font-medium">Payment</th>
                <th className="px-2 py-1.5 text-left font-medium">Status</th>
                <th className="px-2 py-1.5 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => (
                <tr key={r.key} className="border-t">
                  <td className="px-2 py-1.5 font-medium">{r.studentName}</td>
                  <td className="px-2 py-1.5">{r.parentName}</td>
                  <td className="px-2 py-1.5">{fmtDate(r.registeredAt)}</td>
                  <td className="px-2 py-1.5">
                    {r.state === "refunded"
                      ? `Refunded ${money(r.refundedCents)}`
                      : r.state === "paid"
                        ? `Paid ${money(r.amountCents)}`
                        : r.state === "pending"
                          ? "Pending"
                          : "Failed"}
                  </td>
                  <td className="px-2 py-1.5">
                    <StateBadge state={r.state} />
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex justify-end gap-1">
                      <IconBtn label="View registration" onClick={() => setDetail(r)}>
                        <Eye className="h-3.5 w-3.5" />
                      </IconBtn>
                      <IconBtn
                        label="Email parent"
                        onClick={() =>
                          window.open(
                            `mailto:${r.email}?subject=${encodeURIComponent(`${title} — ${selected ?? ""}`)}`,
                            "_blank",
                          )
                        }
                      >
                        <Mail className="h-3.5 w-3.5" />
                      </IconBtn>
                      {isAdmin && (
                        <IconBtn
                          label={
                            r.paymentAmountCents - r.refundedCents <= 0
                              ? `Refund ${r.studentName} — fully refunded, nothing left to refund`
                              : `Refund ${r.studentName}`
                          }
                          disabled={r.paymentAmountCents - r.refundedCents <= 0}
                          onClick={() => setRefundRow(r)}
                        >
                          <DollarSign className="h-3.5 w-3.5" />
                        </IconBtn>
                      )}
                      <IconBtn
                        label="Move to waitlist"
                        onClick={() => setConfirmRemove({ row: r, toWaitlist: true })}
                      >
                        <ListPlus className="h-3.5 w-3.5" />
                      </IconBtn>
                      <IconBtn
                        label="Remove registration"
                        onClick={() => setConfirmRemove({ row: r, toWaitlist: false })}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </IconBtn>
                    </div>
                  </td>
                </tr>
              ))}
              {visibleWaitlist.map((w) => (
                <tr key={`wl:${w.id}`} className="border-t bg-amber-50/40">
                  <td className="px-2 py-1.5 font-medium">{w.child_name}</td>
                  <td className="px-2 py-1.5">{w.adult_name}</td>
                  <td className="px-2 py-1.5">{fmtDate(w.created_at)}</td>
                  <td className="px-2 py-1.5">—</td>
                  <td className="px-2 py-1.5">
                    <Badge variant="outline" className="text-[10px]">Interest</Badge>
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <IconBtn
                      label="Email parent"
                      onClick={() => window.open(`mailto:${w.email}`, "_blank")}
                    >
                      <Mail className="h-3.5 w-3.5" />
                    </IconBtn>
                  </td>
                </tr>
              ))}
              {visibleRows.length === 0 && visibleWaitlist.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-2 py-4 text-center text-muted-foreground">
                    {loading ? "Loading roster…" : "No registrations for this session yet."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Historical registrations — earlier occurrences of a reused session
            label. Preserved as history; never counted in the seats above. */}
        {historicalRows.length > 0 && (
          <div className="rounded-md border bg-muted/20 p-3">
            <div className="text-xs font-medium">
              Historical registrations · earlier sessions ({historicalRows.length})
            </div>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Preserved history from a previous run of this class. These do not occupy seats in the
              session selected above.
            </p>
            <ul className="mt-2 space-y-1">
              {historicalRows.map((h) => (
                <li key={h.id} className="flex flex-wrap justify-between gap-2 text-[11px]">
                  <span>
                    {h.student} · {h.parent} · {h.email}
                  </span>
                  <span className="text-muted-foreground">
                    {h.label} · {new Date(h.created).toLocaleDateString()} · $
                    {(h.amount / 100).toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}


        {/* Financial summary — Financial Ledger */}
        <div className="rounded-md border bg-background/60 p-3">
          <div className="text-xs font-medium">Financial summary · this session</div>
          <div className="mt-2 grid grid-cols-2 gap-y-1 text-xs sm:grid-cols-5">
            <Stat label="Registrations shown" value={String(visibleRows.length)} detail={`${rows.length} total in this session`} />
            <Stat label="Revenue received" value={money(financial.collected)} detail="Collected for this session" />
            <Stat label="Refunds sent" value={money(financial.refunds)} detail="Returned for this session" />
            <Stat label="Payment still due" value={money(financial.outstanding)} detail="Outstanding for this session" />
            <Stat label="Net registration revenue" value={money(financial.net)} detail="Received minus refunds" />
          </div>
          <p className="mt-1 text-[10px] italic text-muted-foreground">
            Read straight from the Financial Ledger.
          </p>
        </div>

        {/* Instructor / payroll — payroll engine */}
        <div className="rounded-md border bg-background/60 p-3">
          <div className="text-xs font-medium">Instructor</div>
          {payroll && payroll.instructors.length > 0 ? (
            <div className="mt-2 space-y-2">
              {payroll.instructors.map((i) => (
                <div key={i.instructor_id} className="grid grid-cols-2 gap-y-1 text-xs sm:grid-cols-4">
                  <Stat label="Instructor" value={i.instructor_name} />
                  <Stat label="Projected payroll" value={money(i.projected_cents)} />
                  <Stat label="Already paid" value={money(i.paid_cents)} />
                  <Stat label="Remaining" value={money(i.remaining_cents)} />
                </div>
              ))}
              <p className="text-[10px] italic text-muted-foreground">
                Read directly from the payroll engine.
              </p>
            </div>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">
              No payroll assignment for this session.
            </p>
          )}
        </div>

        {/* Prorated late enrollment — admin-approved, server-enforced price */}
        <ProratedEnrollmentPanel slug={slug} sessions={sessions} isAdmin={isAdmin} />
      </div>


      {/* View registration */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{detail?.studentName}</DialogTitle>
            <DialogDescription>Registration detail</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-1 text-sm">
              <Line label="Parent" value={detail.parentName} />
              <Line label="Email" value={detail.email} />
              <Line label="Session" value={detail.sessionLabel ?? selected ?? "—"} />
              <Line label="Registered" value={fmtDate(detail.registeredAt)} />
              <Line label="Amount" value={money(detail.amountCents)} />
              <Line label="Refunded" value={money(detail.refundedCents)} />
              <Line label="Status" value={detail.state} />
              <Line label="Registration ID" value={detail.paymentId} />
              <div className="border-t pt-2">
                <div className="mb-1 text-xs font-medium">Refund history</div>
                <RefundHistory paymentId={detail.paymentId} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Refund — PHASE 4A: the only refund entry point. All behaviour goes
          through the admin-refunds service; nothing here writes payments,
          refunds or ledger rows directly. */}
      {refundRow && isAdmin && (
        <RefundDialog
          open={!!refundRow}
          onOpenChange={(o) => !o && setRefundRow(null)}
          payment={{
            id: refundRow.paymentId,
            studentName: refundRow.studentName,
            parentName: refundRow.parentName,
            email: refundRow.email,
            className: title,
            sessionLabel: refundRow.sessionLabel ?? selected,
            amountCents: refundRow.paymentAmountCents,
            alreadyRefundedCents: refundRow.refundedCents,
            stripeSessionId: refundRow.stripeSessionId,
            coveredStudents: rows
              .filter((r) => r.paymentId === refundRow.paymentId)
              .map((r) => r.studentName),
          }}
          onRefunded={() => load()}
        />
      )}




      {/* Remove / waitlist */}
      <Dialog open={!!confirmRemove} onOpenChange={(o) => !o && setConfirmRemove(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirmRemove?.toWaitlist ? "Move to waitlist" : "Remove registration"}
            </DialogTitle>
            <DialogDescription>
              {confirmRemove?.toWaitlist
                ? `${confirmRemove?.row.studentName} will be added to the waitlist and freed from this session's roster.`
                : `${confirmRemove?.row.studentName} will be removed from the roster. Payment and ledger history are preserved.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRemove(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant={confirmRemove?.toWaitlist ? "default" : "destructive"}
              onClick={doRemove}
              disabled={busy}
            >
              {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground leading-relaxed">{label}</div>
      <div className="font-medium text-foreground break-words">{value}</div>
      {detail && <div className="mt-0.5 text-[10px] leading-snug text-muted-foreground">{detail}</div>}
    </div>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium break-all">{value}</span>
    </div>
  );
}

function StateBadge({ state }: { state: PaymentState }) {
  if (state === "paid") return <Badge className="text-[10px]">✓ Confirmed</Badge>;
  if (state === "refunded") return <Badge variant="outline" className="text-[10px]">Refunded</Badge>;
  if (state === "pending") return <Badge variant="secondary" className="text-[10px]">Pending</Badge>;
  return <Badge variant="destructive" className="text-[10px]">Failed</Badge>;
}

function IconBtn({
  label,
  onClick,
  children,
  disabled,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <Button
      size="icon"
      variant="ghost"
      className="h-6 w-6"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}
